import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetCustomerSuccessBenchmarksPayload, ReportFailurePayload, ReportsState, UpdateReportSettingsPayload } from "../types/reports";
import { ICustomerSuccess, IImplementationOverview, IIntegrationsReport, ILivePortfolio, IReportBenchmarks, IReportOverdueWeek, IReportSettings, LivePortfolioRequest } from "../../interfaces/reports.interface";

const emptyPanel = { data: undefined, loading: false, success: false, error: false };
const successPanel = <T,>(data: T) => ({ data, loading: false, success: true, error: false });
const failurePanel = <T,>(payload?: ReportFailurePayload, data?: T) => ({ data, loading: false, success: false, error: true, status: payload?.status });

const initialState: ReportsState = {
    implementationOverview: { ...emptyPanel },
    overdueWeek: { ...emptyPanel },
    integrations: { ...emptyPanel },
    settings: { ...emptyPanel },
    updateSettings: { ...emptyPanel },
    livePortfolio: { ...emptyPanel },
    customerSuccess: { ...emptyPanel },
    customerSuccessBenchmarks: { ...emptyPanel },
};

const reportsSlice = createSlice({
    name: "reports",
    initialState,
    reducers: {
        // Keeps the last figures on screen while a refresh is in flight
        getImplementationOverview(state) {
            state.implementationOverview = { ...state.implementationOverview, loading: true, success: false, error: false };
        },
        getImplementationOverviewSuccess(state, action: PayloadAction<IImplementationOverview>) {
            state.implementationOverview = successPanel(action.payload);
        },
        getImplementationOverviewFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            // A failed refresh keeps the last figures; the page shows an inline error over them
            state.implementationOverview = failurePanel(action.payload, state.implementationOverview.data);
        },

        getOverdueWeek(state, _action: PayloadAction<string>) {
            state.overdueWeek = { data: undefined, loading: true, success: false, error: false };
        },
        getOverdueWeekSuccess(state, action: PayloadAction<IReportOverdueWeek>) {
            state.overdueWeek = successPanel(action.payload);
        },
        getOverdueWeekFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.overdueWeek = failurePanel(action.payload);
        },
        clearOverdueWeek(state) {
            state.overdueWeek = { ...emptyPanel };
        },

        getIntegrationsReport(state) {
            state.integrations = { ...state.integrations, loading: true, success: false, error: false };
        },
        getIntegrationsReportSuccess(state, action: PayloadAction<IIntegrationsReport>) {
            state.integrations = successPanel(action.payload);
        },
        getIntegrationsReportFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            // Same as the overview: a failed refresh keeps the last rows on screen
            state.integrations = failurePanel(action.payload, state.integrations.data);
        },

        getReportSettings(state) {
            state.settings = { ...state.settings, loading: true, success: false, error: false };
        },
        getReportSettingsSuccess(state, action: PayloadAction<IReportSettings>) {
            state.settings = successPanel(action.payload);
        },
        getReportSettingsFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.settings = failurePanel(action.payload);
        },

        updateReportSettings(state, _action: PayloadAction<UpdateReportSettingsPayload>) {
            state.updateSettings = { data: undefined, loading: true, success: false, error: false };
        },
        updateReportSettingsSuccess(state, action: PayloadAction<IReportSettings>) {
            state.updateSettings = successPanel(action.payload);
            state.settings = successPanel(action.payload);
        },
        updateReportSettingsFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.updateSettings = failurePanel(action.payload);
        },

        // Keeps the last period's figures on screen until the new period arrives
        getLivePortfolio(state, _action: PayloadAction<LivePortfolioRequest>) {
            state.livePortfolio = { ...state.livePortfolio, loading: true, success: false, error: false };
        },
        getLivePortfolioSuccess(state, action: PayloadAction<ILivePortfolio>) {
            state.livePortfolio = successPanel(action.payload);
        },
        getLivePortfolioFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.livePortfolio = failurePanel(action.payload, state.livePortfolio.data);
        },

        // Keeps the last figures on screen while a refresh is in flight
        getCustomerSuccess(state) {
            state.customerSuccess = { ...state.customerSuccess, loading: true, success: false, error: false };
        },
        getCustomerSuccessSuccess(state, action: PayloadAction<ICustomerSuccess>) {
            state.customerSuccess = successPanel(action.payload);
        },
        getCustomerSuccessFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.customerSuccess = failurePanel(action.payload, state.customerSuccess.data);
        },

        // Data stays while reloading; the card checks it matches the current selection
        getCustomerSuccessBenchmarks(state, _action: PayloadAction<GetCustomerSuccessBenchmarksPayload>) {
            state.customerSuccessBenchmarks = { ...state.customerSuccessBenchmarks, loading: true, success: false, error: false };
        },
        getCustomerSuccessBenchmarksSuccess(state, action: PayloadAction<IReportBenchmarks>) {
            state.customerSuccessBenchmarks = successPanel(action.payload);
        },
        getCustomerSuccessBenchmarksFailure(state, action: PayloadAction<ReportFailurePayload | undefined>) {
            state.customerSuccessBenchmarks = failurePanel(action.payload, state.customerSuccessBenchmarks.data);
        },
    },
});

export const {
    getImplementationOverview,
    getImplementationOverviewSuccess,
    getImplementationOverviewFailure,
    getOverdueWeek,
    getOverdueWeekSuccess,
    getOverdueWeekFailure,
    clearOverdueWeek,
    getIntegrationsReport,
    getIntegrationsReportSuccess,
    getIntegrationsReportFailure,
    getReportSettings,
    getReportSettingsSuccess,
    getReportSettingsFailure,
    updateReportSettings,
    updateReportSettingsSuccess,
    updateReportSettingsFailure,
    getLivePortfolio,
    getLivePortfolioSuccess,
    getLivePortfolioFailure,
    getCustomerSuccess,
    getCustomerSuccessSuccess,
    getCustomerSuccessFailure,
    getCustomerSuccessBenchmarks,
    getCustomerSuccessBenchmarksSuccess,
    getCustomerSuccessBenchmarksFailure,
} = reportsSlice.actions;

export default reportsSlice.reducer;
